/**
 * Pure helpers for the guided shift flow. The server already orders steps and
 * decides canFinish; these are the client-side safety net and the bits of
 * presentation logic worth unit-testing.
 */

import { compareFlowOrder } from '../Tasks/taskVerification';
import { formatTime } from '../Utils/GeneralUtils';

export { formatTime };

export const HEADSET_METHODS = ['headsets_off', 'headsets_on'];

export const SOP_PREFIX = 'sop:';

/** `sop:<articleId>` for a standalone SOP walkthrough. */
export const sopFlowKind = (articleId) => `${SOP_PREFIX}${articleId}`;

/**
 * `{ type: 'phase' | 'cleaning' | 'sop', phase, articleId }`, or null when the
 * value is not a flow kind. Same four kinds the server accepts.
 */
export const parseFlowKind = (value) => {
    const text = String(value || '').trim();
    if (text === 'open' || text === 'close') return { type: 'phase', phase: text, articleId: '' };
    if (text === 'cleaning') return { type: 'cleaning', phase: '', articleId: '' };
    if (text.startsWith(SOP_PREFIX)) {
        const articleId = text.slice(SOP_PREFIX.length).trim();
        if (articleId) return { type: 'sop', phase: '', articleId };
    }
    return null;
};

const KIND_NOUNS = { open: 'opening', close: 'closing', cleaning: 'cleaning' };

/** The written procedure behind each phase walk; both exist in the mock and in production. */
export const FULL_SOP_ARTICLES = { open: 'sop-gym-opening', close: 'sop-gym-closing' };

export const fullSopArticleId = (kind) => FULL_SOP_ARTICLES[kind] || '';

/** The step-by-step walkthrough of that procedure, or '' for a kind without one. */
export const fullSopPath = (kind) => {
    const articleId = fullSopArticleId(kind);
    return articleId ? `/shift-flow/sop/${encodeURIComponent(articleId)}` : '';
};

/** The noun every button on a flow page is built from. */
export const flowNoun = (kind) => {
    const parsed = parseFlowKind(kind);
    if (!parsed) return String(kind || 'shift');
    if (parsed.type === 'sop') return 'walkthrough';
    return KIND_NOUNS[parsed.type === 'phase' ? parsed.phase : parsed.type];
};

const cap = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/** Start / Resume / complete / Finish copy for one flow kind. */
export const flowLabels = (kind) => {
    const noun = flowNoun(kind);
    return {
        noun,
        title: cap(noun),
        start: `Start ${noun}`,
        resume: `Resume ${noun}`,
        complete: `${cap(noun)} complete`,
        finish: `Finish ${noun}`,
    };
};

/** Banner copy once the offline queue has taken a finish or a step. */
export const offlineSavedLabel = (kind) => `${cap(flowNoun(kind))} saved, will finish when back online`;

/**
 * A queued replay the server refused for good. The trainer is told what the
 * server said, once, rather than left with a walk that quietly never landed.
 */
export const queueRejectedLabel = (kind, message) =>
    `${cap(flowNoun(kind))} could not be finished: ${message}`;

/** Booths a nudge names, as the trainer reads them. */
export const nudgeBoothsLabel = (booths) => {
    const list = (booths || []).filter((b) => b != null);
    if (!list.length) return 'A booth still shows a headset on.';
    if (list.length === 1) return `Booth ${list[0]} still shows a headset on.`;
    return `Booths ${list.join(', ')} still show headsets on.`;
};

const STALE_RUN_CODES = ['E_FLOW_EXPIRED', 'E_FLOW_NOT_STARTED', 'E_FLOW_ALREADY_FINISHED'];

/**
 * The run on screen takes no more work: it expired, it was never started (or
 * another device reset it), or somebody already finished it. Every one of these
 * needs a reload, so the page offers Start again instead of leaving the trainer
 * on an error line the walk cannot get past.
 */
export const isStaleRunError = (code) => STALE_RUN_CODES.includes(code);

export const isHeadsetStep = (step) => HEADSET_METHODS.includes(step?.verification?.method);

export const isPhotoTask = (task) => task?.verification?.method === 'photo';

/** The server samples photos per (date, trainer, task); only an explicit false waives one. */
export const photoRequiredForStep = (step) => isPhotoTask(step) && step.photoRequired !== false;

/**
 * Skipping the photo on a task step still checks the task off, so anything but
 * an optional task with an optional photo owes a reason: without one the server
 * records the work as done with nothing behind it.
 */
export const stepSkipNeedsReason = (step) =>
    step?.requirementLevel !== 'optional'
    || (step?.verification?.photoPolicy || 'required') === 'required';

/**
 * Trainer-facing chip for `completionVerification.status`. Neutral on purpose:
 * a flag goes to a manager for review, it is never "Failed" to the trainer.
 * Empty string means show nothing (not_required, unknown).
 */
export const verificationChipLabel = (status) => {
    switch (status) {
        case 'verified': return 'Verified';
        case 'flagged':
        case 'pending': return 'Sent for review';
        case 'recorded': return 'Photo saved';
        case 'skipped': return 'Photo skipped';
        default: return '';
    }
};

export const verificationChipTone = (status) => {
    switch (status) {
        case 'verified': return 'good';
        case 'flagged':
        case 'pending': return 'review';
        case 'recorded':
        case 'skipped': return 'muted';
        default: return '';
    }
};

export const PHOTO_STAGE_LABELS = { upload: 'Uploading photo', complete: 'Checking photo' };

/** flowOrder ascending, nulls last, then name so ties are stable across reloads. */
export const orderSteps = (steps) => [...(steps || [])].sort(compareFlowOrder);

export const firstOpenStep = (steps) => (steps || []).find((s) => !s.resolved) || null;

export const progress = (steps) => {
    const total = (steps || []).length;
    const resolved = (steps || []).filter((s) => s.resolved).length;
    return { total, resolved, percent: total ? Math.round((resolved / total) * 100) : 0 };
};

/** Same shape DailyReportTasks reports, so the daily report can consume a flow. */
export const summaryForReport = (steps) => {
    const list = steps || [];
    const done = list.filter((s) => s.resolved && (s.myOutcome || s.resolvedBy?.[0]?.outcome || 'done') === 'done');
    const pending = list.filter((s) => !s.resolved);
    return {
        total: list.length,
        completed: done.length,
        pending: pending.length,
        pendingClose: pending.filter((s) => s.shiftPhase === 'close').length,
        completedIds: done.map((s) => s.taskId),
    };
};

const stepOutcome = (step) => step.myOutcome || step.resolvedBy?.[0]?.outcome || null;

/**
 * Mirrors the server: every required step resolved (optional ones may stay
 * open), and no headset step marked done that the heartbeats now contradict
 * (someone powered a booth back on; redo it). A booth that will not turn off is
 * handled by Not needed with a reason, then the finish override.
 */
export const openRequiredSteps = (steps) =>
    (steps || []).filter((s) => !s.resolved && s.requirementLevel !== 'optional');

/**
 * The headset step whose done state the live booth reading contradicts, or
 * null. Only a booth reporting the wrong state counts, the same rule as the
 * server's `_headset_block`: a booth whose PC stopped heartbeating reads
 * `headsetConnected: null` and clears `allHeadsetsOff`, and treating that as a
 * headset coming back on would dead-end the walk on a stale PC.
 */
export const headsetContradiction = (steps, booth) => {
    if (!booth) return null;
    const booths = booth.booths || [];
    return (steps || []).find((s) => {
        if (!isHeadsetStep(s) || stepOutcome(s) !== 'done') return false;
        const wrongState = s.verification.method === 'headsets_off';
        return booths.some((b) => b.headsetConnected === wrongState);
    }) || null;
};

export const canFinish = (steps, booth) => {
    const list = steps || [];
    if (!list.length || openRequiredSteps(list).length) return false;
    return !headsetContradiction(list, booth);
};

/**
 * What the Done button on a headset step should say and whether it is enabled.
 * Ready needs at least one booth actually reporting (`total > 0 && unknown <
 * total`); past that, only a headset still in the wrong state blocks, matching
 * the finish the server will allow. Individual booths not reporting are named
 * by the status panel but never hold the step open, since their PC may not come
 * back tonight.
 *
 * Deliberately stricter than the server's `allHeadsetsOff`, which only needs
 * `total > 0`: no booths in the snapshot, or every booth `unknown` because
 * BoothMinder is down for the gym, is no telemetry at all rather than proof
 * every headset is off, and enabling Done there would attest to a check nothing
 * measured. Both read as waiting.
 */
export const headsetGate = (booth, method) => {
    const summary = booth?.summary;
    const total = Number(summary?.total) || 0;
    const unknown = Number(summary?.unknown) || 0;
    if (!summary || total <= 0 || unknown >= total) {
        return { ready: false, waiting: 0, unknown, label: 'Waiting on booth status' };
    }
    const wantOff = method !== 'headsets_on';
    const { headsetsOn = 0, headsetsOff = 0 } = summary;
    const waiting = wantOff ? headsetsOn : headsetsOff;
    if (waiting === 0) return { ready: true, waiting: 0, unknown, label: 'Done' };
    return { ready: false, waiting, unknown, label: `Waiting on ${waiting} headset${waiting === 1 ? '' : 's'}` };
};

// ---------------------------------------------------------------------------
// SOP walkthrough steps (`sop:<articleId>`). These carry `stepId` and a `state`
// object instead of a task id and a completion, so they need their own helpers.
// ---------------------------------------------------------------------------

/** Done, or skipped with a reason: what the server counts as resolved. */
export const isSopStepResolved = (step) => {
    const state = step?.state || {};
    if (state.done) return true;
    return Boolean(state.skipped && String(state.reason || '').trim());
};

export const orderSopSteps = (steps) => [...(steps || [])].sort((a, b) => {
    const ao = a.order == null ? Number.POSITIVE_INFINITY : Number(a.order);
    const bo = b.order == null ? Number.POSITIVE_INFINITY : Number(b.order);
    if (ao !== bo) return ao - bo;
    return (a.title || '').localeCompare(b.title || '');
});

export const firstOpenSopStep = (steps) => (steps || []).find((s) => !isSopStepResolved(s)) || null;

export const sopProgress = (steps) => {
    const total = (steps || []).length;
    const resolved = (steps || []).filter(isSopStepResolved).length;
    return { total, resolved, percent: total ? Math.round((resolved / total) * 100) : 0 };
};

export const isSopPhotoStep = (step) => step?.evidence?.method === 'photo';

/** A required step, or a required photo, can only be skipped with a reason. */
export const sopSkipNeedsReason = (step) =>
    step?.required !== false || (isSopPhotoStep(step) && (step?.evidence?.photoPolicy || 'required') === 'required');

/** Header chip for a resolved step: 'Done 7:52pm' or 'Skipped: no key'. */
export const sopStepStatusLabel = (step) => {
    const state = step?.state || {};
    if (state.done) return `Done ${formatTime(state.at)}`.trim();
    if (state.skipped) {
        const reason = String(state.reason || '').trim();
        return reason ? `Skipped: ${reason}` : 'Skipped';
    }
    return 'Open';
};

export const firstName = (name) => (name || '').trim().split(/\s+/)[0] || 'a teammate';
